import {
  Avatar,
  Flex,
  ScrollArea,
  Text,
  Tooltip,
  useMantineTheme,
  Modal,
} from "@mantine/core";
import { AddCircle, Profile } from "iconsax-react";
import { IProduct } from "../../interfaces/Products/IProductDetail";
import { useMemo, useState } from "react";
import Search from "../Search";
import { useDebouncedValue, useMediaQuery } from "@mantine/hooks";
import { useTranslation } from "react-i18next";
import CardBoxShadow from "../Cards/CardBoxShadow";

type IProps = {
  product?: IProduct | undefined;
  hasColor?: boolean;
};

export default function BrokersCardsNew({ product, hasColor = true }: IProps) {
  const { t } = useTranslation();
  const isLargerThan991 = useMediaQuery("(max-width: 991px)");
  const theme = useMantineTheme();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 200);
  const organizations = product?.organizations;
  const disabled = !organizations || organizations.length === 0;

  const normalize = (str: string) =>
    str
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();

  const filteredOrganizations = useMemo(() => {
    if (!debouncedSearch) return organizations || [];
    return (organizations || []).filter((organization) =>
      normalize(organization.name).includes(normalize(debouncedSearch))
    );
  }, [debouncedSearch, organizations]);

  return (
    <>
      <CardBoxShadow maxWidth="350px" disabled={disabled}>
        <Flex
          w="100%"
          h="100%"
          p="16px"
          justify={"space-between"}
          direction={"column"}
          gap="16px"
        >
          <Flex gap="20px" align={"center"}>
            <Text
              color={theme.colors.basic[4]}
              fz={"14px"}
              fw={500}
              style={{ whiteSpace: "nowrap" }}
              ff={"barlow"}
              h="22px"
            >
              {t("global.brokersCards.title")}
            </Text>
            <Flex
              onClick={() => (disabled ? null : setOpen(true))}
              style={{
                cursor: disabled ? "default" : "pointer",
              }}
              align={"center"}
              gap={"8px"}
              h="24px"
            >
              <Text
                color={theme.colors.basic[5]}
                fz={"14px"}
                fw={700}
                style={{ whiteSpace: "nowrap" }}
                ff={"barlow"}
                h="22px"
              >
                {t("global.brokersCards.see_all")}
              </Text>
              <AddCircle size={24} color={theme.colors.basic[5]} />
            </Flex>
          </Flex>
          {!!organizations && organizations.length > 0 ? (
            <Flex
              style={{ overflow: "hidden" }}
              pos={"relative"}
              w="100%"
              mih="50px"
              align={"center"}
              gap={"24px"}
            >
              <Flex gap={"24px"} align={"center"} pos="absolute">
                {organizations.map((organization, index) => {
                  return (
                    <Tooltip.Floating
                      label={organization?.name}
                      key={`${organization?.id}-${index}`}
                      zIndex={10}
                    >
                      <span>
                        <Avatar
                          variant="outline"
                          size={50}
                          color="basic.2"
                          radius={8}
                          src={organization?.icon}
                          alt={organization?.name}
                          style={{ transition: "all 0.5s linear" }}
                          styles={{
                            root: {
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              border: `1px solid ${theme.colors.basic[2]}`,
                            },
                            placeholder: {
                              border: `unset`,
                            },
                            image: {
                              height: "40px",
                              width: "40px",
                            },
                          }}
                        >
                          <Profile />
                        </Avatar>
                      </span>
                    </Tooltip.Floating>
                  );
                })}
              </Flex>
            </Flex>
          ) : (
            <Text color="basic.4" fw={400} size={14}>
              {t("global.brokersCards.no_data")}
            </Text>
          )}
        </Flex>
      </CardBoxShadow>
      <Modal
        size="all"
        opened={open}
        onClose={() => setOpen(false)}
        transitionProps={{
          exitDuration: 600,
          transition: "slide-up",
          duration: 600,
          timingFunction: "ease",
        }}
        withCloseButton={false}
        centered
        scrollAreaComponent={ScrollArea.Autosize}
        styles={{
          content: {
            borderRadius: "12px",
            minWidth: "fit-content",
            maxWidth: "fit-content",
          },
          inner: {
            zIndex: 1000,
          },
          overlay: { zIndex: 999 },
          header: {
            marginBottom: "0",
            background: "#F0F0F0",
            padding: "20px 20px",
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            borderBottom: "1px solid #D7D7D7",
            gap: "24px",
            minWidth: "100%",
          },
          root: {
            minWidth: "100%",
            maxWidth: "800px",
          },
          close: {
            display: "flex",
            height: "28px",
            width: "28px",
            justifyContent: "center",
            alignItems: "center",
            svg: {
              height: "24px !important",
              width: "24px !important",
            },
          },
          body: {
            width: "90vw",
            minWidth: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            backgroundColor: "#fff",
            borderRadius: "12px",
            padding: "0",
            maxWidth: "800px",
          },
        }}
      >
        <Modal.Header>
          <Flex direction={"column"} w="100%" gap="16px">
            <Flex
              justify={"space-between"}
              align={"center"}
              w="100%"
              gap="24px"
            >
              <Text
                style={{
                  color: "#2F2F2F",
                  fontSize: "18px",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                {t("global.brokersCards.all")}
              </Text>
              <Flex
                justify={"space-between"}
                align={"center"}
                w="fit"
                gap="24px"
              >
                {isLargerThan991 ? null : (
                  <Search
                    placeholder={t("global.brokersCards.search")}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onClick={() => setSearch("")}
                  />
                )}
                <Modal.CloseButton />
              </Flex>
            </Flex>
            {isLargerThan991 ? (
              <Search
                placeholder={t("global.brokersCards.search")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onClick={() => setSearch("")}
              />
            ) : null}
          </Flex>
        </Modal.Header>
        <Modal.Body>
          <Flex gap="16px" w="100%" wrap={"wrap"} p="15px 20px" rowGap="24px">
            {filteredOrganizations.map((organization, index) => {
              return (
                <Tooltip
                  label={organization?.name}
                  key={`${organization?.id}-${index}`}
                  zIndex={10}
                  position="bottom"
                  withArrow
                >
                  <span>
                    <Avatar
                      variant="outline"
                      size={hasColor ? 80 : 48}
                      color="basic.2"
                      radius={8}
                      src={organization?.icon}
                      alt={organization?.name}
                      style={{ transition: "all 0.5s linear" }}
                      styles={{
                        root: {
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          border: `1px solid ${theme.colors.basic[2]}`,
                        },
                        placeholder: {
                          border: `unset`,
                        },
                        image: {
                          height: hasColor ? "70px" : "38px",
                          width: hasColor ? "70px" : "38px",
                        },
                      }}
                    >
                      <Profile />
                    </Avatar>
                  </span>
                </Tooltip>
              );
            })}
          </Flex>
        </Modal.Body>
      </Modal>
    </>
  );
}
